import { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Layout from '../shared/Layout';
import PageHeader from '../shared/PageHeader';
import Select from '../../components/solid-forms/Select';
import DraftReview from './DraftReview';
import { createQuestion, saveConnector } from '../conversations-creator-canvas/api';
import '../../css/conversations.css';
import '../../css/conversations-ai.css';

const LANGUAGE_OPTIONS = [
  { id: 'українською', name: 'Українською' },
  { id: 'англійською', name: 'Англійською' },
  { id: 'польською', name: 'Польською' },
];

function parseOptionId(value) {
  if (value === '') return '';
  const parsed = Number(value);
  return Number.isNaN(parsed) ? '' : parsed;
}

export default function ConversationsAiGenerator() {
  const navigate = useNavigate();
  const [step, setStep] = useState('form');
  const [error, setError] = useState('');

  const [levels, setLevels] = useState([]);
  const [conversationTypes, setConversationTypes] = useState([]);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [topic, setTopic] = useState('');
  const [level, setLevel] = useState('');
  const [typeId, setTypeId] = useState('');
  const [questionCount, setQuestionCount] = useState(5);
  const [language, setLanguage] = useState('українською');

  const [questions, setQuestions] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    axios.get('/api/levels', { withCredentials: true }).then(r => setLevels(r.data || [])).catch(() => {});
    axios.get('/api/conversationtypes', { withCredentials: true }).then(r => setConversationTypes(r.data || [])).catch(() => {});
  }, []);

  const generate = (e) => {
    e.preventDefault();
    if (!topic.trim()) return;
    setError('');
    setStep('generating');
    axios.post('/api/ai/generate-quiz-draft', JSON.stringify({
      topic, level: level || null, typeId: typeId || null, questionCount: Number(questionCount) || 5, language,
    }), { withCredentials: true, headers: { 'Content-Type': 'application/json' } })
      .then(response => {
        setQuestions(response.data || []);
        setStep('review');
      })
      .catch(err => {
        setError((err.response && err.response.data) || 'Не вдалося згенерувати тест. Спробуйте ще раз.');
        setStep('form');
      });
  };

  const commit = () => {
    if (questions.length === 0) return;
    setIsSaving(true);
    setError('');

    const conversationPayload = {
      name: name || topic,
      description,
      typeId: typeId || null,
      level: level || null,
      isPresent: 2,
      price: 0,
    };

    axios.post('/api/conversations', JSON.stringify(conversationPayload), {
      withCredentials: true, headers: { 'Content-Type': 'application/json' },
    }).then(async (conversationResponse) => {
      const conversationId = conversationResponse.data.id;
      const questionIds = [];

      for (const question of questions) {
        const created = await createQuestion({ text: question.text, conversationId });
        questionIds.push(created.data.id);
      }

      for (let i = 0; i < questions.length; i++) {
        const fromId = questionIds[i];
        const nextId = i < questions.length - 1 ? questionIds[i + 1] : questionIds[i];
        for (const option of questions[i].options) {
          await saveConnector({
            text: option.text,
            fromId,
            nextId,
            isRight: option.isRight ? 1 : 2,
            clasification: null,
            estimationGroup: null,
            point: option.points || 0,
            stage: null,
          });
        }
      }

      const lastQuestionId = questionIds[questionIds.length - 1];
      await axios.put('/api/answers/' + lastQuestionId, { id: lastQuestionId, isLast: 1 }, {
        withCredentials: true, headers: { 'Content-Type': 'application/json' },
      });

      navigate('/conversations-creator-canvas/' + conversationId);
    }).catch(err => {
      console.error(err);
      setError('Не вдалося зберегти тест. Спробуйте ще раз.');
      setIsSaving(false);
    });
  };

  return (
    <Layout>
      <PageHeader name="Генератор тестів AI" />
      <main className="main-block">
        <div className="main-content txt-c ai-generator-page">
          {step === 'form' ? (
            <div className="ai-generator-card">
              <div className="ai-generator-intro">
                <h3>Створіть тест за допомогою AI</h3>
                <p>Опишіть тему — AI згенерує питання з варіантами відповідей, які можна відредагувати перед збереженням.</p>
              </div>

              <form className="ai-generator-form" onSubmit={generate}>
                {error ? <p className="ai-generator-error">{error}</p> : ''}
                <div className="ai-generator-field ai-generator-field-topic">
                  <label>Тема тесту</label>
                  <textarea value={topic} onChange={e => setTopic(e.target.value)} required
                    placeholder="Напр.: Present Simple, подорожі, їжа..." />
                </div>
                <div className="ai-generator-row">
                  <div className="ai-generator-field">
                    <label>Назва тесту (необов'язково)</label>
                    <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder={topic || 'За темою'} />
                  </div>
                  <div className="ai-generator-field">
                    <label>Опис (необов'язково)</label>
                    <textarea value={description} onChange={e => setDescription(e.target.value)} />
                  </div>
                </div>
                <div className="ai-generator-row ai-generator-row-compact">
                  <div className="ai-generator-field">
                    <label>Рівень</label>
                    <Select options={levels} info="Будь-який" fieldName="name" value={level}
                      handleInputChange={value => setLevel(parseOptionId(value))}
                      className="ai-select" inputName="level" />
                  </div>
                  <div className="ai-generator-field">
                    <label>Тип розмови</label>
                    <Select options={conversationTypes} info="Будь-який" fieldName="name" value={typeId}
                      handleInputChange={value => setTypeId(parseOptionId(value))}
                      className="ai-select" inputName="typeId" />
                  </div>
                  <div className="ai-generator-field">
                    <label>Кількість питань</label>
                    <input type="number" min="1" max="20" value={questionCount} onChange={e => setQuestionCount(e.target.value)} />
                  </div>
                  <div className="ai-generator-field">
                    <label>Мова</label>
                    <Select options={LANGUAGE_OPTIONS} info="Мова" fieldName="name" value={language}
                      handleInputChange={value => setLanguage(value)}
                      className="ai-select" inputName="language" />
                  </div>
                </div>
                <button type="submit" className="ai-generator-submit">Згенерувати тест</button>
              </form>
            </div>
          ) : ''}

          {step === 'generating' ? (
            <div className="ai-generator-card ai-generator-loading">
              <div className="ai-generator-spinner"></div>
              <p>Генерую тест за допомогою AI...</p>
              <p className="ai-generator-loading-hint">Це може зайняти до хвилини (обмеження AI-сервісу).</p>
            </div>
          ) : ''}

          {step === 'review' ? (
            <div className="ai-generator-card">
              <div className="ai-generator-intro">
                <h3>Перевірте та відредагуйте</h3>
                <p>Знайдено {questions.length} питань. Позначте правильні відповіді, приберіть зайве або додайте свої варіанти.</p>
              </div>
              {error ? <p className="ai-generator-error">{error}</p> : ''}
              <DraftReview questions={questions} onChange={setQuestions} />
              <div className="ai-generator-actions">
                <button type="button" className="ai-generator-submit" disabled={isSaving} onClick={commit}>
                  {isSaving ? 'Зберігаю...' : 'Створити тест'}
                </button>
                <button type="button" className="ai-generator-cancel" disabled={isSaving} onClick={() => setStep('form')}>
                  Назад
                </button>
              </div>
            </div>
          ) : ''}
        </div>
      </main>
    </Layout>
  );
}
